import { createContext, getOwner, useContext } from "solid-js";
import { reconcile } from "solid-js/store";

import assert from "@repo/utils/assert";
import FeatureStore from "@repo/utils-solid/FeatureStore";
import Organization from "@repo/vygor-models/Organization";

import Cart from "~/components/checkout/Cart";

import { Membership, useMemberships } from "./memberships";
import { PaymentMethod } from "./paymentMethods";
import { Promotion, usePromotions } from "./promotions";

const initialState = {
  paymentMethodId: undefined as PaymentMethod.Id | undefined,
  promoCodeId: undefined as Promotion.Code.Id | undefined,
};

export const { Context, Provider, useMembershipCheckout } = FeatureStore.init(
  "MembershipCheckout",
  initialState,
  ([store, setStore], { withLogger }) => {
    const [, setMemberships] = useMemberships();
    const [promotions, setPromotions] = usePromotions();

    const owner = getOwner()!;

    const context = useContext(CheckoutContext);
    assert(
      context,
      "CheckoutContext is required when calling useMembershipCheckout.",
    );

    const accessors = {
      paymentMethodId: () => store.paymentMethodId,
      promoCode: () => promotions.codes.byId(store.promoCodeId),
      cart: () =>
        Cart.applyPromoCode(
          [
            {
              itemId: context.planId,
              quantity: 1,
            },
          ],
          accessors.promoCode(),
          owner,
        ),
      total: () => Cart.total(accessors.cart(), owner),
      unusedPromoCode: () => {
        if (!store.promoCodeId) return false;
        if (
          accessors
            .cart()
            .some((entry) => entry.promoCodeId === store.promoCodeId)
        )
          return false;
        return true;
      },
    };

    const mutations = {
      paymentMethodId(paymentMethodId: PaymentMethod.Id) {
        setStore("paymentMethodId", paymentMethodId);
      },
      clearPromoCode() {
        setStore("promoCodeId", undefined);
      },
    };

    const actions = withLogger("action", {
      ...mutations,
      applyPromoCode(orgId: Organization.Id, code: string) {
        return setPromotions.forOrganization
          .checkPromoCode(orgId, code)
          .then((promoCode) => {
            setStore("promoCodeId", promoCode.id);
          });
      },
      submit() {
        const paymentMethodId = accessors.paymentMethodId();
        assert(paymentMethodId, "Payment method ID is required.");

        const [membershipEntry] = accessors.cart();
        assert(membershipEntry, "Expected membership cart entry.");

        return setMemberships
          .create(context.planId, paymentMethodId, membershipEntry.promoCodeId)
          .then((res) => {
            setStore(reconcile(initialState));
            return res;
          });
      },
    });

    return { accessors, actions };
  },
);

export const CheckoutContext = createContext<{
  planId: Membership.Plan.Id;
}>();
